import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Steps, Card, Typography, Checkbox, message, Row, Col, Divider } from 'antd';
import { BankOutlined, UserOutlined, CheckCircleOutlined, RocketOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

interface RegistrationFormData {
  // Company details
  companyName: string;
  industryType: string;
  companySize: string;
  country: string;
  
  // Admin user details
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  
  // Terms
  acceptTerms: boolean;
  acceptPrivacy: boolean;
}

const RegistrationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<RegistrationFormData>({
    companyName: '',
    industryType: '',
    companySize: '',
    country: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    acceptPrivacy: false
  });

  const steps = [
    {
      title: t('registration.steps.company'),
      icon: <BankOutlined />,
    },
    {
      title: t('registration.steps.admin'),
      icon: <UserOutlined />,
    },
    {
      title: t('registration.steps.confirm'),
      icon: <CheckCircleOutlined />,
    },
  ];

  const industryOptions = [
    { value: 'technology', label: t('industries.technology', { defaultValue: 'Technology' }) },
    { value: 'healthcare', label: t('industries.healthcare', { defaultValue: 'Healthcare' }) },
    { value: 'financial_services', label: t('industries.financialServices', { defaultValue: 'Financial Services' }) },
    { value: 'manufacturing', label: t('industries.manufacturing', { defaultValue: 'Manufacturing' }) },
    { value: 'retail', label: t('industries.retail', { defaultValue: 'Retail' }) },
    { value: 'education', label: t('industries.education', { defaultValue: 'Education' }) },
    { value: 'government', label: t('industries.government', { defaultValue: 'Government' }) },
    { value: 'non_profit', label: t('industries.nonProfit', { defaultValue: 'Non-Profit' }) },
    { value: 'other', label: t('industries.other', { defaultValue: 'Other' }) },
  ];

  const companySizeOptions = [
    { value: '50-100', label: t('companySize.50-100', { defaultValue: '50-100 employees' }) },
    { value: '101-500', label: t('companySize.101-500', { defaultValue: '101-500 employees' }) },
    { value: '501-1000', label: t('companySize.501-1000', { defaultValue: '501-1000 employees' }) },
    { value: '1000+', label: t('companySize.1000+', { defaultValue: '1000+ employees' }) },
  ];

  const countryOptions = [
    { value: 'DE', label: t('countries.germany', { defaultValue: 'Germany' }) },
    { value: 'FR', label: t('countries.france', { defaultValue: 'France' }) },
    { value: 'IT', label: t('countries.italy', { defaultValue: 'Italy' }) },
    { value: 'ES', label: t('countries.spain', { defaultValue: 'Spain' }) },
    { value: 'PL', label: t('countries.poland', { defaultValue: 'Poland' }) },
    { value: 'FI', label: t('countries.finland', { defaultValue: 'Finland' }) },
    { value: 'UK', label: t('countries.uk', { defaultValue: 'United Kingdom' }) },
    { value: 'other', label: t('countries.other', { defaultValue: 'Other' }) },
  ];

  // Update form values when step changes
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      form.setFieldsValue(formData);
    }
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = async () => {
    try {
      // Define fields for each step
      const stepFields = {
        0: ['companyName', 'industryType', 'companySize', 'country'],
        1: ['firstName', 'lastName', 'email', 'password', 'confirmPassword'],
        2: ['acceptTerms', 'acceptPrivacy']
      };
      
      // Get current step fields
      const currentStepFields = stepFields[currentStep as keyof typeof stepFields];
      
      // Validate current step fields
      await form.validateFields(currentStepFields);
      
      console.log('Form data at step', currentStep, ':', formData);
      
      if (currentStep === steps.length - 1) {
        await handleSubmit(formData);
      } else {
        setCurrentStep(currentStep + 1);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (data: RegistrationFormData) => {
    setLoading(true);
    try {
      // Step 1: Create organization
      const { data: orgResult, error: orgError } = await supabase.rpc('register_organization_simple', {
        company_name: data.companyName,
        industry_type: data.industryType,
        company_size: data.companySize,
        country: data.country,
      });

      if (orgError) throw orgError;

      // Step 2: Sign up user with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            full_name: `${data.firstName} ${data.lastName}`,
            organization_id: orgResult.organization_id,
            role: 'admin',
          },
          emailRedirectTo: `${window.location.origin}/auth/login`,
        },
      });

      if (authError) throw authError;

      // Step 3: Complete user registration
      if (authData.user) {
        const { error: userError } = await supabase.rpc('complete_user_registration', {
          user_id: authData.user.id,
          organization_id: orgResult.organization_id,
          full_name: `${data.firstName} ${data.lastName}`,
          role: 'admin',
        });

        if (userError) {
          console.error('User registration error:', userError);
          // Don't throw - user is created in auth, we can fix profile later
        }
      }

      message.success(t('registration.success'));
      
      // Show success message and redirect
      setTimeout(() => {
        navigate('/auth/login', { 
          state: { 
            email: data.email,
            message: t('registration.successMessage'),
            organizationSlug: orgResult.slug,
          } 
        });
      }, 2000);
    } catch (error) {
      console.error('Registration error:', error);
      message.error((error as Error).message || t('registration.error'));
    } finally {
      setLoading(false);
    }
  };

  const renderCompanyStep = () => (
    <div className="space-y-6">
        <div>
          <Title level={4}>{t('registration.companyDetails')}</Title>
          <Paragraph className="text-gray-600">
            {t('registration.companyDetailsDesc')}
          </Paragraph>
        </div>

        <Form.Item
          name="companyName"
          label={t('registration.companyName')}
          rules={[
            { required: true, message: t('validation.required') },
            { min: 3, message: t('validation.minLength', { min: 3 }) },
          ]}
        >
          <Input size="large" placeholder={t('registration.companyNamePlaceholder')} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="industryType"
              label={t('registration.industry')}
              rules={[{ required: true, message: t('validation.required') }]}
            >
              <Select
                size="large"
                placeholder={t('registration.selectIndustry')}
                options={industryOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="companySize"
              label={t('registration.companySize')}
              rules={[{ required: true, message: t('validation.required') }]}
            >
              <Select
                size="large"
                placeholder={t('registration.selectSize')}
                options={companySizeOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="country"
          label={t('registration.country')}
          rules={[{ required: true, message: t('validation.required') }]}
        >
          <Select
            size="large"
            placeholder={t('registration.selectCountry')}
            options={countryOptions}
          />
        </Form.Item>
      </div>
  );

  const renderAdminStep = () => (
    <div className="space-y-6">
        <div>
          <Title level={4}>{t('registration.adminAccount')}</Title>
          <Paragraph className="text-gray-600">
            {t('registration.adminAccountDesc')}
          </Paragraph>
        </div>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="firstName"
              label={t('registration.firstName')}
              rules={[{ required: true, message: t('validation.required') }]}
            >
              <Input size="large" placeholder={t('registration.firstNamePlaceholder')} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="lastName"
              label={t('registration.lastName')}
              rules={[{ required: true, message: t('validation.required') }]}
            >
              <Input size="large" placeholder={t('registration.lastNamePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="email"
          label={t('registration.email')}
          rules={[
            { required: true, message: t('validation.required') },
            { type: 'email', message: t('validation.email') },
          ]}
        >
          <Input size="large" placeholder={t('registration.emailPlaceholder')} />
        </Form.Item>

        <Form.Item
          name="password"
          label={t('registration.password')}
          rules={[
            { required: true, message: t('validation.required') },
            { min: 8, message: t('validation.minLength', { min: 8 }) },
          ]}
        >
          <Input.Password size="large" placeholder={t('registration.passwordPlaceholder')} />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={t('registration.confirmPassword')}
          dependencies={['password']}
          rules={[
            { required: true, message: t('validation.required') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('validation.passwordMismatch')));
              },
            }),
          ]}
        >
          <Input.Password size="large" placeholder={t('registration.confirmPasswordPlaceholder')} />
        </Form.Item>
      </div>
  );

  const renderConfirmStep = () => {
    return (
      <div className="space-y-6">
        <div>
          <Title level={4}>{t('registration.reviewAndConfirm')}</Title>
          <Paragraph className="text-gray-600">
            {t('registration.reviewAndConfirmDesc')}
          </Paragraph>
        </div>

        <Card className="bg-gray-50">
          <div className="space-y-4">
            <div>
              <Text strong>{t('registration.companyDetails')}</Text>
              <div className="mt-2 space-y-1">
                <div><Text type="secondary">{t('registration.companyName')}:</Text> <Text strong>{formData.companyName || '-'}</Text></div>
                <div><Text type="secondary">{t('registration.industry')}:</Text> <Text strong>{industryOptions.find(i => i.value === formData.industryType)?.label || formData.industryType || '-'}</Text></div>
                <div><Text type="secondary">{t('registration.companySize')}:</Text> <Text strong>{formData.companySize || '-'}</Text></div>
                <div><Text type="secondary">{t('registration.country')}:</Text> <Text strong>{countryOptions.find(c => c.value === formData.country)?.label || formData.country || '-'}</Text></div>
              </div>
            </div>

            <Divider />

            <div>
              <Text strong>{t('registration.adminAccount')}</Text>
              <div className="mt-2 space-y-1">
                <div><Text type="secondary">{t('registration.name')}:</Text> <Text strong>{formData.firstName || '-'} {formData.lastName || '-'}</Text></div>
                <div><Text type="secondary">{t('registration.email')}:</Text> <Text strong>{formData.email || '-'}</Text></div>
              </div>
            </div>
          </div>
        </Card>

        <div className="space-y-3">
          <Form.Item
            name="acceptTerms"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error(t('validation.acceptTerms'))),
              },
            ]}
          >
            <Checkbox>
              {t('registration.acceptTerms')} <a href="/terms" target="_blank">{t('registration.termsOfService')}</a>
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="acceptPrivacy"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error(t('validation.acceptPrivacy'))),
              },
            ]}
          >
            <Checkbox>
              {t('registration.acceptPrivacy')} <a href="/privacy" target="_blank">{t('registration.privacyPolicy')}</a>
            </Checkbox>
          </Form.Item>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderCompanyStep();
      case 1:
        return renderAdminStep();
      case 2:
        return renderConfirmStep();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl shadow-2xl">
        <div className="p-8">
          <div className="text-center mb-8">
            <RocketOutlined className="text-purple-600 mb-4" style={{ fontSize: '64px' }} />
            <Title level={2}>{t('registration.title')}</Title>
            <Paragraph className="text-gray-600 text-lg">
              {t('registration.subtitle')}
            </Paragraph>
          </div>

          <Steps current={currentStep} className="mb-8">
            {steps.map((step, index) => (
              <Step key={index} title={step.title} icon={step.icon} />
            ))}
          </Steps>

          <Form
            form={form}
            layout="vertical"
            initialValues={formData}
            onValuesChange={(_, allValues) => {
              setFormData(prev => ({ ...prev, ...allValues }));
            }}
          >
            {renderStepContent()}
          </Form>

          <div className="mt-8 flex justify-between">
            <Button
              size="large"
              onClick={handlePrev}
              disabled={currentStep === 0}
            >
              {t('common.previous')}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleNext}
              loading={loading}
            >
              {currentStep === steps.length - 1 ? t('registration.createAccount') : t('common.next')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default RegistrationPage;